import { Link } from 'react-router-dom'
import { GlassCard } from '@/components/ui/glass-card'
import { ArrowRight } from 'lucide-react'

export default function ResourcesPage() {
  const guides = [
    {
      href: '/resources/underwriting-basics',
      title: 'How We Stress-Test Every Deal',
      description: 'The underwriting framework we run before any offer',
      slug: 'underwriting-basics',
    },
    {
      href: '/resources/financing-options',
      title: 'Financing the First Few Deals',
      description: 'Loan types we use and when each one fits',
      slug: 'financing-options',
    },
    {
      href: '/resources/deal-criteria',
      title: 'What Disqualifies a Deal in 5 Minutes',
      description: 'The criteria we screen against before underwriting',
      slug: 'deal-criteria',
    },
    {
      href: '/resources/due-diligence-checklist',
      title: 'Pre-Close Due Diligence Checklist',
      description: 'What we verify between offer and closing',
      slug: 'due-diligence-checklist',
    },
    {
      href: '/resources/cleveland-overview',
      title: 'Why Cleveland (Most Coastal Investors Skip It)',
      description: 'The market thesis in numbers, not vibes',
      slug: 'cleveland-overview',
    },
    {
      href: '/resources/property-comparison-sheet',
      title: 'Property Comparison Sheet',
      description: 'Side-by-side scorecard for short-listing deals',
      slug: 'property-comparison-sheet',
    },
    {
      href: '/resources/rental-calculator',
      title: 'Full Rental Property Analyzer',
      description: 'Cash flow, cap rate, DSCR, and adverse-scenario stress',
      slug: 'rental-calculator',
    },
  ]

  const calculators = [
    {
      href: '/resources/calculators/mortgage-payment',
      title: 'Mortgage Payment',
      description: 'Calculate monthly P&I payments',
    },
    {
      href: '/resources/calculators/cash-on-cash',
      title: 'Cash-on-Cash Return',
      description: 'Measure your return on invested cash',
    },
    {
      href: '/resources/calculators/dscr',
      title: 'DSCR Calculator',
      description: 'Check if rental income covers debt',
    },
    {
      href: '/resources/calculators/rent-vs-buy',
      title: 'Rent vs Buy',
      description: 'Compare renting vs buying costs',
    },
    {
      href: '/resources/calculators/brrrr',
      title: 'BRRRR Strategy',
      description: 'Model the BRRRR strategy returns',
    },
  ]

  return (
    <div className="bg-background text-foreground">
      {/* Hero Section */}
      <section className="border-border border-b">
        <div className="mx-auto max-w-5xl px-5 pt-14 pb-12 md:px-8 md:pt-20 md:pb-16">
          <h1 className="text-fluid-h1 text-navy mb-6 max-w-4xl">Investor resources</h1>
          <p className="text-fluid-body-lg text-muted-foreground max-w-2xl">
            Tools and guides to help you become a smarter real estate investor.
          </p>
        </div>
      </section>

      {/* Guides Section */}
      <section className="px-5 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="mb-8 flex items-center gap-3">
            <h2 className="text-fluid-h2 text-foreground">Guides & Articles</h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {guides.map((guide) => {
              return (
                <Link key={guide.href} to={guide.href} className="guide-card group">
                  <GlassCard hover className="h-full">
                    <div className="flex items-start gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-foreground group-hover:text-primary text-lg font-semibold transition-colors">
                            {guide.title}
                          </h3>
                          <ArrowRight className="text-muted-foreground group-hover:text-primary h-4 w-4 shrink-0 transition-all group-hover:translate-x-1" />
                        </div>
                        <p className="text-muted-foreground mt-2">{guide.description}</p>
                      </div>
                    </div>
                  </GlassCard>
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-5xl" />

      {/* Calculators Section */}
      <section className="px-5 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="mb-8 flex items-center gap-3">
            <h2 className="text-fluid-h2 text-foreground">Calculators</h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {calculators.map((calc) => (
              <Link key={calc.href} to={calc.href} className="calc-card group">
                <GlassCard hover className="h-full text-center">
                  <h3 className="text-foreground group-hover:text-primary mb-2 text-lg font-semibold transition-colors">
                    {calc.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">{calc.description}</p>
                </GlassCard>
              </Link>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/resources/calculators"
              className="text-primary hover:text-primary/80 inline-flex items-center gap-2 font-medium transition-colors"
            >
              View all calculators
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-5 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-5xl">
          <GlassCard accent className="text-center">
            <h2 className="text-fluid-h3 text-foreground mb-4">Ready to Put Your Knowledge to Work?</h2>
            <p className="text-muted-foreground mx-auto mb-6 max-w-2xl text-lg">
              Join our investor list to receive deal alerts and exclusive investment opportunities.
            </p>
            <Link
              to="/contact"
              className="gradient-gold text-primary-foreground hover:glow-gold-sm inline-flex items-center gap-2 rounded-lg px-6 py-3 font-semibold transition-shadow"
            >
              Join the Investor List
              <ArrowRight className="h-5 w-5" />
            </Link>
          </GlassCard>
        </div>
      </section>
    </div>
  )
}
